import Toast from '../components/Toast';

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import confetti from 'canvas-confetti';
import './Checkout.css';

const Checkout = () => {
  const { cart, getTotals, clearCart, orderType, setOrderType } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: localStorage.getItem('user_phone') || '',
    address: '',
    city: 'Sangrur',
    pincode: '148001',
    deliveryDate: '',
    deliveryTime: '',
    specialInstructions: '',
    deliveryType: orderType || 'delivery' // Default to context value
  });

  // Get totals based on current delivery type from formData
  const totals = getTotals(formData?.deliveryType || 'delivery');

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Coupon State
  const [coupons, setCoupons] = useState([]);
  const [dateDisplay, setDateDisplay] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const res = await fetch('https://panel.supplebakes.com/api/coupons/get-all.php?active_only=true');
      const data = await res.json();
      if (data.success) {
        setCoupons(data.coupons);
      }
    } catch (err) {
      console.error('Error loading coupons:', err);
    }
  };

  const fireConfetti = () => {
    const defaults = {
      spread: 350,
      ticks: 80,
      gravity: 0.7,
      decay: 0.94,
      startVelocity: 50,
      shapes: ["square"],
      colors: ["#7f3f98", "#f89a3a", "#994cbb"], // Added # for hex
    };

    const shoot = () => {
      confetti({
        ...defaults,
        particleCount: 80, // Reduced slightly for mobile performance
        scalar: 1,
        shapes: ["square"],
        origin: { x: 0, y: 1 }
      });
      confetti({
        ...defaults,
        particleCount: 40,
        scalar: 0.75,
        shapes: ["square"],
        origin: { x: 0, y: 1 }
      });

      confetti({
        ...defaults,
        particleCount: 80,
        scalar: 1,
        shapes: ["square"],
        origin: { x: 1, y: 1 }
      });
      confetti({
        ...defaults,
        particleCount: 40,
        scalar: 0.75,
        shapes: ["square"],
        origin: { x: 1, y: 1 }
      });
    };

    setTimeout(shoot, 0);
    setTimeout(shoot, 100);
    setTimeout(shoot, 200);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showNotification('Please enter a coupon code', 'error');
      return;
    }

    setIsValidatingCoupon(true);

    const cartData = {
      subtotal: Number(totals.subtotal),
      items: cart.items.map(item => {
        const itemData = {
          cake_id: item.cakeId,
          quantity: item.quantity,
          subtotal: Number(item.subtotal)
        };
        // Only include category_id if present
        if (item.categoryId || item.category_id) {
          itemData.category_id = item.categoryId || item.category_id;
        }
        return itemData;
      })
    };

    try {
      const res = await fetch('https://panel.supplebakes.com/api/coupons/validate-apply.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.toUpperCase(),
          cart: cartData,
          user_phone: formData.phone.replace(/\D/g, ''),
          applied_coupons: appliedCoupon ? [appliedCoupon] : []
        })
      });

      const data = await res.json();

      if (data.success) {
        setAppliedCoupon(data.coupon);
        setDiscount(parseFloat(data.discount_amount));
        fireConfetti();
        showNotification(`Coupon applied! You saved ₹${data.discount_amount}`);
      } else {
        console.error('Coupon validation failed:', data);
        showNotification(data.error || data.message || 'Invalid coupon', 'error');
        setAppliedCoupon(null);
        setDiscount(0);
      }
    } catch (err) {
      console.error('Coupon validation error:', err);
      showNotification('Failed to apply coupon', 'error');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponCode('');
    showNotification('Coupon removed');
  };

  const finalTotal = Math.max(0, totals.total - discount);

  const recordCouponUsage = async (orderId, billingId) => {
    if (!appliedCoupon) return;

    try {
      await fetch('https://panel.supplebakes.com/api/coupons/record-usage.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_id: appliedCoupon.id,
          coupon_code: appliedCoupon.code,
          discount_amount: discount,
          cart_total_before: totals.total,
          cart_total_after: finalTotal,
          user_phone: formData.phone,
          user_name: formData.name,
          order_id: orderId,
          billing_id: billingId || orderId // Fallback if billing_id missing
        })
      });
    } catch (err) {
      console.error('Error recording coupon usage:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'deliveryType') {
      setOrderType(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing (or changing delivery type)
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    
    // Address validation only if delivery type is 'delivery'
    if (formData.deliveryType === 'delivery') {
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.pincode.trim()) {
        newErrors.pincode = 'Pincode is required';
      } else if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Pincode must be 6 digits';
      }
    }

    if (!formData.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (!formData.deliveryTime) newErrors.deliveryTime = 'Delivery time is required';

    setErrors(newErrors);
    return newErrors;
  };

  const showNotification = (message, type = 'success') => {
    if (window.cordova && window.plugins && window.plugins.toast) {
      window.plugins.toast.show(message, 'long', 'center');
    } else {
      setToast({ message, type });
    }
  };

  const handleCloseToast = () => {
    setToast(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      const missingFields = Object.keys(validationErrors).join(', ');
      showNotification(`Required fields missing: ${missingFields}`, 'error');
      // showNotification('Please fill in all required fields', 'error');
      return;
    }

    setIsProcessing(true);

    // Prepare cart items for API
    const orderItems = cart.items.map(item => {
      if (item.type === 'addon') {
        // Addon item structure
        return {
          type: 'addon',
          addonId: item.addonId,
          addonName: item.addonName,
          addonUnitType: item.addonUnitType,
          addonUnit: item.addonUnit,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          subtotal: item.subtotal
        };
      } else {
        // Cake item structure
        return {
          type: 'cake',
          cakeId: item.cakeId,
          category_id: item.categoryId,
          cakeTitle: item.cakeTitle,
          weight: item.weight,
          flavourId: item.flavourId || 0,
          flavourName: item.flavourName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          subtotal: item.subtotal,
          cake_message: item.cakeMessage || ''
        };
      }
    });

    // Prepare API payload
    const orderPayload = {
      customer_name: formData.name,
      customer_phone: formData.phone,
      customer_phone: formData.phone,
      delivery_address: formData.deliveryType === 'pickup' ? 'Store Pickup' : `${formData.address}, ${formData.city}, ${formData.pincode}`,
      delivery_date: formData.deliveryDate,
      delivery_time: formData.deliveryTime,
      delivery_type: formData.deliveryType, // 'delivery' or 'pickup'
      special_instructions: formData.deliveryType === 'pickup' ? `[STORE PICKUP] ${formData.specialInstructions}` : formData.specialInstructions,
      payment_method: 'razorpay',
      cart_items: orderItems,
      subtotal: totals.subtotal,
      tax: totals.tax,
      delivery_fee: totals.deliveryFee,
      total: finalTotal, // Use the discounted total
      discount_amount: discount,
      coupon_code: appliedCoupon ? appliedCoupon.code : null
    };

    try {
      // 1. Create Order on Server (and get Razorpay Order ID)
      const createRes = await fetch('https://panel.supplebakes.com/order/create-order.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderPayload)
      });

      const orderData = await createRes.json();

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create order');
      }

      // 2. Initialize Razorpay with Order ID
      const { data } = orderData;
      const options = {
        key: 'rzp_live_Rn9bkPNFYU7vVh',
        amount: data.amount, // Amount from server (in paise)
        currency: data.currency || 'INR',
        name: 'Supple Bakes',
        description: 'Cake Order Payment',
        image: 'https://supplebakes.com/wp-content/uploads/2023/01/supple_bakes_cropped_logo.png',
        order_id: data.razorpay_order_id, // Critical: Pass the Order ID obtained from your server
        prefill: {
          name: formData.name,
          contact: formData.phone,
          email: 'no-reply@supplebakes.com'
        },
        theme: {
          color: '#9c6b5f'
        },
        handler: async function (response) {
          // 3. Verify Payment on Server
          try {
            const verifyRes = await fetch('https://panel.supplebakes.com/order/verify-payment.php', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                db_order_id: data.order_id // Pass the database order ID to update status
              })
            });

            const verifyData = await verifyRes.json();

            if (verifyData.success) {
              // Record coupon usage
              if (appliedCoupon) {
                 await recordCouponUsage(data.order_id, data.billing_id || data.order_id); 
              }

              showNotification('🎉 Order placed successfully!');
              clearCart();
              setTimeout(() => {
                navigate('/order-success', {
                  state: {
                    orderId: data.order_number,
                    paymentId: response.razorpay_payment_id,
                    amount: finalTotal,
                    date: new Date().toISOString()
                  }
                });
              }, 1000);
            } else {
              showNotification(verifyData.message || 'Payment verification failed', 'error');
              setIsProcessing(false);
            }
          } catch (err) {
            console.error('Verification Error:', err);
            showNotification('Network error during verification. Please contact support.', 'error');
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            showNotification('Payment cancelled', 'info');
            setIsProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response){
        console.error('Payment Failed:', response.error);
        showNotification(`Payment Failed: ${response.error.description}`, 'error');
        setIsProcessing(false);
      });

      rzp.open();

    } catch (error) {
      console.error('Order Flow Error:', error);
      showNotification(error.message || 'Failed to initiate order', 'error');
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/');
    } else {
      const userPhone = localStorage.getItem('user_phone');
      if (!userPhone) {
        navigate('/login');
      }
    }
  }, [cart.items.length, navigate]);

  if (cart.items.length === 0) return null;
  const userPhone = localStorage.getItem('user_phone');
  if (!userPhone) return null;

  // Get minimum date (today)
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];

  // Check if selected date is today
  const isToday = formData.deliveryDate === minDate;
  
  // Get current hour for validation
  const currentHour = new Date().getHours();
  
  // Time slots configuration
  const timeSlots = [
    { start: 12, label: 'Morning (9AM - 12PM)', value: 'Morning (9AM - 12PM)' },
    { start: 16, label: 'Afternoon (12PM - 4PM)', value: 'Afternoon (12PM - 4PM)' },
    { start: 20, label: 'Evening (4PM - 8PM)', value: 'Evening (4PM - 8PM)' }
  ];

  // Helper to check availability
  // Available if current time is before the END of the slot? Or somewhat before start?
  // User logic previously was: Morning < 12, Afternoon < 16, Evening < 20.
  // This means you can order Morning slot until 11:59 AM.
  const isSlotAvailable = (cutoffHour) => {
    if (!isToday) return true;
    return currentHour < cutoffHour; 
  };

  return (
    <div className="checkout-page">
      <header className="checkout-header glass-effect">
        <div className="checkout-header-content">
          <Link to="/cart" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="checkout-title">Checkout</h1>
        </div>
      </header>

      <div className="checkout-content">
        <div className="container">
          <div className="checkout-layout">
            <div className="checkout-form-section">
              <form onSubmit={handleSubmit} className="checkout-form">
                {/* Delivery Method Selector - Standalone Section */}
                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Select Delivery Method
                  </h2>

                  <div className="delivery-type-selector">
                    <button 
                      type="button"
                      className={`delivery-type-btn ${formData.deliveryType === 'delivery' ? 'active' : ''}`}
                      onClick={() => handleChange({ target: { name: 'deliveryType', value: 'delivery' }})}
                    >
                      <svg className="delivery-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      Delivery
                    </button>
                    <button 
                      type="button"
                      className={`delivery-type-btn ${formData.deliveryType === 'pickup' ? 'active' : ''}`}
                      onClick={() => handleChange({ target: { name: 'deliveryType', value: 'pickup' }})}
                    >
                      <svg className="delivery-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                      </svg>
                      Pickup
                    </button>
                  </div>
                </div>

                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Contact Information
                  </h2>

                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-input ${errors.name ? 'error' : ''}`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <span className="error-message">{errors.name}</span>}
                    </div>



                    <div className="form-group">
                      <label className="form-label">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={!!localStorage.getItem('user_phone')}
                        className={`form-input ${errors.phone ? 'error' : ''}`}
                        placeholder="10-digit mobile number"
                        style={localStorage.getItem('user_phone') ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                      {errors.phone && <span className="error-message">{errors.phone}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    {formData.deliveryType === 'pickup' ? 'Store Location' : 'Delivery Address'}
                  </h2>

                  {formData.deliveryType === 'delivery' ? (
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="form-label">Street Address *</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`form-textarea ${errors.address ? 'error' : ''}`}
                        placeholder="House/Flat No., Street Name, Area"
                        rows="3"
                      ></textarea>
                      {errors.address && <span className="error-message">{errors.address}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`form-input ${errors.city ? 'error' : ''}`}
                        placeholder="City name"
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                      />
                      {errors.city && <span className="error-message">{errors.city}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Pincode *</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        className={`form-input ${errors.pincode ? 'error' : ''}`}
                        placeholder="6-digit pincode"
                        maxLength="6"
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                      />
                      {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                    </div>
                  </div>
                  ) : (
                    <div className="store-pickup-info" style={{ 
                      padding: '1rem', 
                      background: '#f9f9f9', 
                      borderRadius: '8px',
                      border: '1px solid #eee',
                      color: '#666',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                     }}>
                      <p>📍 You can pick up your order from our store location.</p>
                      <p style={{ marginTop: '0.5rem' }}>Store Address: Supple Bakes Opp. City park (BSNL PARK), outside nabha gate, Sangrur - 148001</p>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <h2 className="section-heading">
                    <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Delivery Schedule
                  </h2>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Delivery Date *</label>
                      <input
                        type="date"
                        name="deliveryDate"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        className={`form-input ${errors.deliveryDate ? 'error' : ''}`}
                        min={minDate}
                      />
                      {errors.deliveryDate && <span className="error-message">{errors.deliveryDate}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Delivery Time *</label>
                      <select
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={handleChange}
                        className={`form-select ${errors.deliveryTime ? 'error' : ''}`}
                      >
                        <option value="">Select time slot</option>
                        {timeSlots.map(slot => {
                          const available = isSlotAvailable(slot.start);
                          return (
                            <option 
                              key={slot.start} 
                              value={slot.value} 
                              disabled={!available}
                            >
                              {slot.label} {!available && '(Passed)'}
                            </option>
                          );
                        })}
                      </select>
                      {errors.deliveryTime && <span className="error-message">{errors.deliveryTime}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label className="form-label">Special Instructions (Optional)</label>
                      <textarea
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Any special requests or instructions for delivery"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <button type="submit" className="submit-btn btn-primary ripple" disabled={isProcessing}>
                  {isProcessing ? 'Processing...' : `Place Order - ₹${finalTotal.toFixed(2)}`}
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="order-summary-sidebar">
              <div className="order-summary card">
                <h2 className="summary-title">Order Summary</h2>
                
                {/* Coupon Section */}
                <div className="coupon-summary-section" style={{ marginBottom: '1.5rem' }}>
                  {!appliedCoupon ? (
                    <div className="coupon-input-group">
                      <input 
                        type="text" 
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Coupon Code"
                        className="form-input"
                        style={{ padding: '0.8rem', fontSize: '0.9rem' }}
                      />
                      <button 
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={isValidatingCoupon || !couponCode}
                        className="btn-primary"
                        style={{ padding: '0 1.5rem', fontSize: '0.9rem', whiteSpace: 'nowrap' }}
                      >
                        {isValidatingCoupon ? '...' : 'Apply'}
                      </button>
                    </div>
                  ) : (
                    <div className="applied-coupon-info" style={{ 
                        background: '#e8f5e9', 
                        padding: '0.75rem', 
                        borderRadius: '8px',
                        border: '1px solid #c8e6c9',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ color: '#2e7d32', fontWeight: 'bold' }}>{appliedCoupon.code}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          Saved ₹{discount}
                        </div>
                      </div>
                      <button 
                        type="button" 
                        onClick={handleRemoveCoupon}
                        style={{ color: '#c62828', background: 'none', border: 'none', fontSize: '0.8rem', fontWeight: 'bold' }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  
                  {/* Available Coupons Hint */}
                  {!appliedCoupon && coupons.length > 0 && (
                    <div className="available-coupons-hint" style={{ marginTop: '0.5rem' }}>
                      <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Available Coupons:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {coupons.map(c => (
                          <span 
                            key={c.id} 
                            onClick={() => setCouponCode(c.code)}
                            style={{ 
                              fontSize: '0.75rem', 
                              padding: '2px 8px', 
                              background: '#fff3e0', 
                              border: '1px dashed #ff9800',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              color: '#e65100'
                            }}
                          >
                            {c.code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="summary-items">
                  {cart.items.map(item => (
                    <div key={item.id} className="summary-item">
                      <div className="summary-item-details">
                        <div className="summary-item-name">
                          {item.type === 'addon' ? item.addonName : item.cakeTitle}
                        </div>
                        {item.cakeMessage && (
                          <div className="summary-item-message" style={{ color: '#9c6b5f', fontSize: '0.8rem', fontStyle: 'italic' }}>
                            "{item.cakeMessage}"
                          </div>
                        )}
                        <div className="summary-item-meta">
                          {item.type === 'addon' ? (
                            // Addon meta information
                            <>
                              {item.categoryName} • Qty: {item.quantity}
                            </>
                          ) : (
                            // Cake meta information
                            <>
                              {item.weight}kg • {item.flavourName} • Qty: {item.quantity}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="summary-item-price">₹{item.subtotal.toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                <div className="summary-divider"></div>

                <div className="summary-rows">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₹{totals.subtotal.toLocaleString()}</span>
                  </div>

                  {totals.tax > 0 && (
                    <div className="summary-row">
                      <span>Tax ({totals.settings?.tax_percentage}%)</span>
                      <span>₹{totals.tax.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="summary-row">
                    <span>Delivery Fee</span>
                    <span>
                      {totals.deliveryFee === 0 ? (
                         <span style={{ color: 'green' }}>Free</span>
                      ) : (
                         `₹${totals.deliveryFee}`
                      )}
                    </span>
                  </div>
                  {discount > 0 && (
                    <div className="summary-row" style={{ color: '#2e7d32' }}>
                      <span>Discount</span>
                      <span>-₹{discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-total">
                    <span>Total</span>
                    <span className="total-value">₹{finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />
      )}
    </div>
  );
};

export default Checkout;
